import React, { useMemo, useState } from 'react';
import { useRememberedState } from '../../hooks/useRemembered';
import {
    AlertTriangle, Check, Euro, ExternalLink, HelpCircle, Loader2, Receipt, RefreshCw, RotateCcw,
    ShieldAlert, ShieldCheck, X, Zap,
} from 'lucide-react';
import { Button } from '../Button';
import { useIntegrityFindings } from '../../hooks/useIntegrityFindings';
import type { StoredIntegrityFinding } from '../../services/integrity/integrityService';
import type { Abrechnungslage, IntegritySeverity } from '../../services/integrity/integrityTypes';
import { useUnbookedSentReports } from '../../hooks/useUnbookedSentReports';
import { UNBOOKED_RULE_ID } from '../../services/integrity/integrityLogic';
import { ReportRebookDialog } from './ReportRebookDialog';
import { ohneArchivierte } from '../../services/integrity/archivFilter';
import { sofortNachbuchen } from '../../services/reportRebookService';
import type { ListProject } from '../../services/listProject';

interface Props {
    /** Dieselbe (firmengefilterte) Liste wie die uebrigen Reiter — nur fuer den Sprung ins Projekt. */
    projects: ListProject[];
    onOpenProject: (project: ListProject, isReadOnly: boolean) => void;
}

/**
 * Der Integritaetswaechter — die Ansicht (MIGRATIONS.md Nr. 56).
 *
 * NUR ANZEIGE. Repariert wird im Projekt, gerechnet wird im Nachtlauf; hier
 * steht das Gedaechtnis: seit wann faellt etwas auf, wer hat gesagt, dass es
 * so in Ordnung ist, und hat sich seither etwas veraendert. Der Projektcheck
 * beantwortet dieselben Fragen bereits fuer den Augenblick — was ihm fehlt,
 * ist die Zeit dazwischen.
 *
 * Der wichtigste Zustand dieser Ansicht ist WIEDER AUFGELEBT: ein quittierter
 * Befund, dessen Fingerabdruck sich geaendert hat. Eine Quittierung gilt nur
 * fuer den Zahlenstand, den sie gesehen hat — waechst die Abweichung, ist
 * „gesehen und in Ordnung" ein Deckel ueber einer groesser werdenden Luecke.
 * Deshalb ist so ein Befund rot, steht oben und laesst sich vom Schalter
 * „Quittierte ausblenden" NICHT verstecken.
 */

const SCHWEREN: IntegritySeverity[] = ['kritisch', 'warnung', 'hinweis'];

const SCHWERE_META: Record<IntegritySeverity, { text: string; rang: number; badge: string; kachel: string; zahl: string }> = {
    kritisch: {
        text: 'Kritisch',
        rang: 0,
        badge: 'bg-rose-100 text-rose-700 border-rose-200',
        kachel: 'border-rose-200 bg-rose-50',
        zahl: 'text-rose-700',
    },
    warnung: {
        text: 'Warnung',
        rang: 1,
        badge: 'bg-amber-100 text-amber-700 border-amber-200',
        kachel: 'border-amber-200 bg-amber-50',
        zahl: 'text-amber-700',
    },
    hinweis: {
        text: 'Hinweis',
        rang: 2,
        badge: 'bg-slate-100 text-slate-600 border-slate-200',
        kachel: 'border-slate-200 bg-slate-50',
        zahl: 'text-slate-700',
    },
};

const datum = (iso?: string | null): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('de-DE', { dateStyle: 'medium' });
};

const tageSeit = (iso?: string | null): number | null => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    const heute = new Date();
    heute.setHours(0, 0, 0, 0);
    return Math.max(0, Math.round((heute.getTime() - d.getTime()) / 86400000));
};

/** „seit 6 Tagen" — die Angabe, die es vorher nirgends gab. */
const seitText = (iso?: string | null): string => {
    const t = tageSeit(iso);
    if (t === null) return 'Erstsichtung unbekannt';
    if (t === 0) return 'heute zuerst gesehen';
    if (t === 1) return 'seit 1 Tag';
    return `seit ${t} Tagen`;
};

const istQuittiert = (f: StoredIntegrityFinding): boolean => !!f.acknowledgedAt;

/**
 * Wieder aufgelebt: quittiert, aber der Zahlenstand von damals passt nicht
 * mehr. Fehlt `acknowledged_fingerprint` (Altzeile aus einer frueheren
 * Fassung), gilt der Befund als quittiert und NICHT als aufgelebt — lieber
 * eine stille Zeile als ein Fehlalarm, der die echten uebertoent.
 */
const istWiederAufgelebt = (f: StoredIntegrityFinding): boolean =>
    !!f.acknowledgedAt && !!f.acknowledgedFingerprint && f.acknowledgedFingerprint !== f.fingerprint;

/** Offen ist, was niemand quittiert hat — und alles, was wieder aufgelebt ist. */
const istOffen = (f: StoredIntegrityFinding): boolean => !istQuittiert(f) || istWiederAufgelebt(f);

const zahlText = (n?: number): string =>
    n === undefined ? '—' : n.toLocaleString('de-DE', { maximumFractionDigits: 3 });

/**
 * Tagesdatum als TT.MM.JJJJ. Die Abrechnungslage kommt als ISO-Tag aus dem
 * Nachtlauf; ein ISO-String in der Oberflaeche waere hier besonders teuer,
 * weil er neben deutschen Datumsangaben steht und der Bearbeiter beim
 * Ueberfliegen Monat und Tag vertauscht.
 */
const kurzTag = (iso?: string | null): string | null => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/**
 * „01.07.–31.07.2026": die Jahreszahl steht nur einmal, wenn beide Tage im
 * selben Jahr liegen. Der Zeitraum steht in JEDER Zeile — zweimal dieselbe
 * Jahreszahl kostet genau den Platz, den die Rechnungsnummer daneben braucht.
 * Fehlt eine der beiden Grenzen, wird die vorhandene allein genannt statt eine
 * offene Spanne zu erfinden.
 */
const zeitraumText = (von?: string, bis?: string): string | null => {
    const a = kurzTag(von);
    const b = kurzTag(bis);
    if (a && b) {
        // Beide im selben Jahr? Dann vorne nur Tag und Monat.
        return a.slice(6) === b.slice(6) ? `${a.slice(0, 6)}–${b}` : `${a}–${b}`;
    }
    return a || b || null;
};

const STAENDE: Abrechnungslage['stand'][] = ['abgerechnet', 'kein_beleg', 'unbekannt'];

/**
 * Die Abrechnungslage eines Befundes, wie sie der Nachtlauf als
 * `details.abrechnung` an die Zeile haengt.
 *
 * DEFENSIV — und zwar als Normalfall, nicht als Sonderfall: solange der
 * naechste Lauf nicht durch ist, traegt KEINE der bestehenden Zeilen dieses
 * Feld. `details` ist eine jsonb-Spalte und damit genau das, was irgendwann
 * einmal hineingeschrieben wurde; ein ungepruefter Zugriff nimmt die ganze
 * Verwaltung mit. Alles, was nicht zweifelsfrei als Stand zu erkennen ist,
 * gilt deshalb als `unbekannt` — nie als „kein Beleg", denn das waere eine
 * offene Forderung, die niemand geprueft hat.
 */
const abrechnungslage = (f: StoredIntegrityFinding): Abrechnungslage => {
    const roh: unknown = f.details?.abrechnung;
    if (!roh || typeof roh !== 'object' || Array.isArray(roh)) return { stand: 'unbekannt' };
    const o = roh as Record<string, unknown>;
    const stand = STAENDE.find((s) => s === o.stand);
    if (!stand) return { stand: 'unbekannt' };
    const txt = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined);
    return { stand, beleg: txt(o.beleg), von: txt(o.von), bis: txt(o.bis), stichtag: txt(o.stichtag) };
};

type AbrechnungFilter = 'alle' | 'kein_beleg' | 'abgerechnet';

const ABRECHNUNG_FILTER: { wert: AbrechnungFilter; text: string }[] = [
    { wert: 'alle', text: 'Alle' },
    { wert: 'kein_beleg', text: 'Nur ohne Beleg' },
    { wert: 'abgerechnet', text: 'Nur abgerechnet' },
];

/**
 * Das Kennzeichen der Abrechnungslage — steht in der Zeile selbst und NICHT in
 * einem Aufklappbereich. Die Frage „ist der Zeitraum schon abgerechnet?"
 * entscheidet, ob eine Zeile Geld wert ist; sie erst nach einem Klick zu
 * beantworten hiesse, sie 1227-mal zu klicken.
 *
 * Die drei Zustaende sind bewusst DREI und nicht zwei:
 *  - abgerechnet: eine Tatsache, keine Bewertung — deshalb ruhiges Grau mit
 *    Beleg und Zeitraum. Weder rot (es ist kein Fehler) noch gruen (es ist
 *    keine Entwarnung: der Befund bleibt ein Befund, er ist nur bezahlt).
 *  - kein_beleg: der Fall, in dem noch Geld zu holen ist — er darf auffallen.
 *  - unbekannt: eine FEHLENDE ANGABE. Gedaempft und ohne Rahmen, damit sie
 *    weder wie eine Entwarnung noch wie eine offene Forderung aussieht. Eine
 *    fehlende Antwort als Antwort zu zeigen ist genau die stille Fehlaussage,
 *    gegen die dieses Modul gebaut ist.
 */
const AbrechnungKennzeichen: React.FC<{ lage: Abrechnungslage }> = ({ lage }) => {
    const zeitraum = zeitraumText(lage.von, lage.bis);
    const stichtag = kurzTag(lage.stichtag);

    if (lage.stand === 'abgerechnet') {
        return (
            <p className="mt-2 inline-flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-[11px] font-bold text-slate-700">
                <Receipt className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                <span>Zeitraum abgerechnet</span>
                {lage.beleg && (
                    <>
                        <span className="text-slate-300">·</span>
                        <span className="font-mono">{lage.beleg}</span>
                    </>
                )}
                {zeitraum && (
                    <>
                        <span className="text-slate-300">·</span>
                        <span className="font-medium">{zeitraum}</span>
                    </>
                )}
            </p>
        );
    }

    if (lage.stand === 'kein_beleg') {
        return (
            <p className="mt-2 inline-flex flex-wrap items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] font-bold text-amber-800">
                <Euro className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                <span>kein Beleg über diesen Zeitraum</span>
                {zeitraum ? (
                    <>
                        <span className="text-amber-300">·</span>
                        <span className="font-medium text-amber-700">{zeitraum}</span>
                    </>
                ) : stichtag ? (
                    <>
                        <span className="text-amber-300">·</span>
                        <span className="font-medium text-amber-700">geprüft zum {stichtag}</span>
                    </>
                ) : null}
            </p>
        );
    }

    return (
        <p
            className="mt-2 inline-flex items-center gap-1.5 text-[11px] italic text-slate-400"
            title="Der Nachtlauf hat zu diesem Befund noch keine Abrechnungslage geschrieben. Ob der Zeitraum abgerechnet ist, steht im Projekt."
        >
            <HelpCircle className="w-3.5 h-3.5 shrink-0" /> Abrechnung unbekannt
        </p>
    );
};

export const AdminIntegrityView: React.FC<Props> = ({ projects, onOpenProject }) => {
    const { findings, isLoading, error, busyId, reload, acknowledge } = useIntegrityFindings();
    const [zeigeQuittierte, setZeigeQuittierte] = useRememberedState('admin.integrity.quittierte', false);
    /**
     * Archivierte Projekte (`status = finished`) sind VORGABE ausgeblendet —
     * ihre Befunde sind Arbeit von gestern und uebertoenen die laufenden. Der
     * Nachtlauf erinnert sie weiter (Regel `meldetNoch`), nur der Schirm zeigt
     * sie nicht; der Schalter holt sie samt Zahl zurueck.
     */
    const [zeigeArchivierte, setZeigeArchivierte] = useRememberedState('admin.integrity.archivierte', false);
    /** Vorgabe „alle" — gefiltert wird auf Wunsch, nicht ungefragt. */
    const [abrechnungFilter, setAbrechnungFilter] = useRememberedState<AbrechnungFilter>('admin.integrity.abrechnung', 'alle');
    const [quittung, setQuittung] = useState<{ id: string; grund: string } | null>(null);
    const [fehler, setFehler] = useState<string | null>(null);

    /**
     * Die GRUNDMENGE dieser Ansicht. Alle Kopfzahlen, Filterzahlen und die
     * Liste rechnen auf derselben Menge — sonst verspricht eine Kachel „12
     * kritisch" und die Liste zeigt drei.
     */
    const archiv = useMemo(() => ohneArchivierte(findings, projects), [findings, projects]);
    const grundmenge = zeigeArchivierte ? findings : archiv.sichtbar;

    const sichtbar = useMemo(
        () => grundmenge.filter((f) => {
            if (!zeigeQuittierte && !istOffen(f)) return false;
            if (abrechnungFilter === 'alle') return true;
            // `unbekannt` faellt bei beiden Filtern heraus — es ist weder das
            // eine noch das andere. Dass dadurch etwas fehlt, sagt der Hinweis
            // unter den Filterknoepfen ausdruecklich.
            return abrechnungslage(f).stand === abrechnungFilter;
        }),
        [grundmenge, zeigeQuittierte, abrechnungFilter],
    );

    /** Kopfzahlen zaehlen OFFENE Befunde — quittierte sind Arbeit von gestern. */
    const kopfzahlen = useMemo(() => {
        const zaehler: Record<IntegritySeverity, number> = { kritisch: 0, warnung: 0, hinweis: 0 };
        grundmenge.filter(istOffen).forEach((f) => {
            if (zaehler[f.severity] !== undefined) zaehler[f.severity] += 1;
        });
        return zaehler;
    }, [grundmenge]);

    /**
     * Wie viele der OFFENEN Befunde in einem bereits abgerechneten Zeitraum
     * liegen. Die Zahl gehoert neben die Schweren, weil sie die Dringlichkeit
     * umdreht: ein kritischer Befund ueber einen laengst bezahlten Zeitraum ist
     * eine Buchhaltungsaufgabe, ein kritischer Befund ohne Beleg ist offenes
     * Geld. Bezogen auf dieselbe Grundmenge wie `kopfzahlen` — sonst stuenden
     * hier vier Zahlen, die verschiedene Dinge zaehlen.
     */
    const abgerechnetAnzahl = useMemo(
        () => grundmenge.filter((f) => istOffen(f) && abrechnungslage(f).stand === 'abgerechnet').length,
        [grundmenge],
    );

    /**
     * Die Zahlen an den Filterknoepfen — auf DEMSELBEN Vorrat wie die Liste
     * (also inklusive/exklusive Quittierter je nach Schalter), sonst verspricht
     * ein Knopf Zeilen, die er anschliessend nicht zeigt.
     */
    const abrechnungZahlen = useMemo(() => {
        const basis = grundmenge.filter((f) => zeigeQuittierte || istOffen(f));
        const z: Record<AbrechnungFilter | 'unbekannt', number> = {
            alle: basis.length, kein_beleg: 0, abgerechnet: 0, unbekannt: 0,
        };
        basis.forEach((f) => { z[abrechnungslage(f).stand] += 1; });
        return z;
    }, [grundmenge, zeigeQuittierte]);

    const aufgelebteAnzahl = useMemo(() => grundmenge.filter(istWiederAufgelebt).length, [grundmenge]);
    const quittierteAnzahl = useMemo(
        () => grundmenge.filter((f) => istQuittiert(f) && !istWiederAufgelebt(f)).length,
        [grundmenge],
    );

    /**
     * Gruppierung nach Projekt. Sortiert wird nach dem SCHLIMMSTEN Befund des
     * Projekts, nicht nach Anzahl: zwanzig Hinweise sind harmloser als ein
     * kritischer Befund, und wer die Liste von oben abarbeitet, soll das
     * Teuerste zuerst sehen.
     */
    const gruppen = useMemo(() => {
        const map = new Map<string, {
            projectId: string;
            nummer: string;
            name: string;
            befunde: StoredIntegrityFinding[];
        }>();

        sichtbar.forEach((f) => {
            let g = map.get(f.projectId);
            if (!g) {
                g = {
                    projectId: f.projectId,
                    nummer: f.projectNumber || 'Keine Nr.',
                    name: f.projectName || 'Unbenannt',
                    befunde: [],
                };
                map.set(f.projectId, g);
            }
            g.befunde.push(f);
        });

        const gewicht = (f: StoredIntegrityFinding) =>
            (istWiederAufgelebt(f) ? -1 : SCHWERE_META[f.severity]?.rang ?? 9);

        const liste = Array.from(map.values());
        liste.forEach((g) => {
            g.befunde.sort((a, b) => {
                const d = gewicht(a) - gewicht(b);
                if (d !== 0) return d;
                // Bei gleicher Schwere zuerst der aelteste — er steht am
                // laengsten offen und ist am ehesten liegen geblieben.
                return (a.firstSeenAt || '').localeCompare(b.firstSeenAt || '');
            });
        });

        liste.sort((a, b) => {
            const d = gewicht(a.befunde[0]) - gewicht(b.befunde[0]);
            if (d !== 0) return d;
            return a.nummer.localeCompare(b.nummer, 'de', { numeric: true });
        });

        return liste;
    }, [sichtbar]);

    const rohesProjekt = (projectId: string) => projects.find((p) => p?.id === projectId);

    /**
     * Sprung ins Projekt — derselbe Weg wie in „Offene Positionen": das ROHE
     * Projektobjekt geht an `onOpenProject`, und zwar schreibgeschuetzt.
     * Diese Ansicht repariert nichts; sie schickt einen hin, der es kann.
     */
    const springen = (projectId: string) => {
        const p = rohesProjekt(projectId);
        if (p) onOpenProject(p, true);
    };

    // Die Meldungszeilen zum Nachbuchen. Der Befund traegt zwar die
    // report_history_id (`scopeId`), aber nicht den Begehungstag und nicht den
    // Abschnitt — beides braucht der Buchungsdialog, und beides steht in der
    // Sicht. Deshalb hier derselbe Ladepfad wie in der Meldungsliste.
    const { rows: unbookedRows, markExempt, reload: reloadUnbooked } = useUnbookedSentReports();
    const [rebookScopeId, setRebookScopeId] = useState<string | null>(null);
    const rebookRow = useMemo(
        () => unbookedRows.find((r) => r.report_history_id === rebookScopeId) || null,
        [unbookedRows, rebookScopeId],
    );

    /**
     * SCHNELLWEG. Ein Klick bucht, wenn die Vorschau nichts anzumerken hat;
     * sonst geht genau dieser Fall ins Fenster, mit seinen Hinweisen. Das
     * Ergebnis bleibt UNTER dem Befund stehen — eine Meldung, die im selben
     * Moment verschwindet, ist verschluckt.
     */
    const [sofortId, setSofortId] = useState<string | null>(null);
    const [sofortMeldung, setSofortMeldung] = useState<Record<string, { ok: boolean; text: string }>>({});

    const sofortBuchen = async (f: StoredIntegrityFinding) => {
        const row = unbookedRows.find((r) => r.report_history_id === f.scopeId);
        if (!row) return;
        setSofortId(f.id);
        setSofortMeldung((m) => { const n = { ...m }; delete n[f.id]; return n; });
        try {
            const r = await sofortNachbuchen(row, f.details?.abrechnung as any);
            if (r.status === 'dialog') {
                const hinweise = r.preview.blocker.length + r.preview.warnungen.length;
                setSofortMeldung((m) => ({
                    ...m,
                    [f.id]: {
                        ok: false,
                        text: `Nicht ohne Rückfrage buchbar (${hinweise} ${hinweise === 1 ? 'Hinweis' : 'Hinweise'}) — bitte im Fenster prüfen.`,
                    },
                }));
                setRebookScopeId(f.scopeId);
                return;
            }
            // `same_report` ist bei EINER Zielposition der erwartete
            // Idempotenzfall und steht schon in der Hauptmeldung.
            const zeigen = r.ergebnis.skipped.filter((s) => s.reason !== 'same_report');
            setSofortMeldung((m) => ({
                ...m,
                [f.id]: {
                    ok: r.status === 'gebucht',
                    text: [r.ergebnis.message, ...zeigen.map((s) => s.message)].filter(Boolean).join('\n\n'),
                },
            }));
            if (r.status === 'gebucht') { void reloadUnbooked(); void reload(); }
        } catch (e: any) {
            setSofortMeldung((m) => ({ ...m, [f.id]: { ok: false, text: e?.message || 'Nachbuchen fehlgeschlagen.' } }));
        } finally {
            setSofortId(null);
        }
    };

    const quittieren = async (f: StoredIntegrityFinding, grund: string) => {
        setFehler(null);
        try {
            await acknowledge(f.id, grund.trim());
            setQuittung(null);
        } catch (e: any) {
            setFehler(e?.message || 'Der Befund konnte nicht quittiert werden.');
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                        <ShieldAlert className="w-5 h-5 text-rose-600" /> Integritätswächter
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        Befunde des Nachtlaufs mit Gedächtnis: seit wann sie offen sind und wer sie
                        abgezeichnet hat. Repariert wird im Projekt.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 text-xs font-bold text-slate-600">
                        <input
                            type="checkbox"
                            checked={zeigeQuittierte}
                            onChange={(e) => setZeigeQuittierte(e.target.checked)}
                            className="rounded border-slate-300"
                        />
                        Quittierte anzeigen{quittierteAnzahl > 0 ? ` (${quittierteAnzahl})` : ''}
                    </label>
                    <label
                        className="flex items-center gap-2 text-xs font-bold text-slate-600"
                        title='Projekte im Archiv (Status „Abgeschlossen“). Der Nachtlauf prüft sie weiter; hier sind sie nur ausgeblendet.'
                    >
                        <input
                            type="checkbox"
                            checked={zeigeArchivierte}
                            onChange={(e) => setZeigeArchivierte(e.target.checked)}
                            className="rounded border-slate-300"
                        />
                        Archivierte anzeigen{archiv.ausgeblendet > 0 ? ` (${archiv.ausgeblendet})` : ''}
                    </label>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => void reload()}
                        icon={<RefreshCw className="w-4 h-4" />}
                    >
                        Aktualisieren
                    </Button>
                </div>
            </div>

            {/* Kopfzahlen je Schwere — plus die Abrechnungslage als vierte Zahl */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {SCHWEREN.map((s) => (
                    <div key={s} className={`rounded-2xl border p-4 ${SCHWERE_META[s].kachel}`}>
                        <p className={`text-2xl font-extrabold ${SCHWERE_META[s].zahl}`}>{kopfzahlen[s]}</p>
                        <p className="text-xs font-bold text-slate-600 mt-0.5">{SCHWERE_META[s].text}</p>
                    </div>
                ))}
                {/* Ruhige Kachel: eine Tatsache neben drei Bewertungen. */}
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-2xl font-extrabold text-slate-700">{abgerechnetAnzahl}</p>
                    <p className="text-xs font-bold text-slate-600 mt-0.5">davon Zeitraum abgerechnet</p>
                </div>
            </div>

            {/* Filter über die Abrechnungslage. Vorgabe „Alle". */}
            <div className="rounded-2xl border border-slate-200 bg-white p-3">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mr-1">
                        Abrechnung
                    </span>
                    {ABRECHNUNG_FILTER.map((o) => (
                        <button
                            key={o.wert}
                            type="button"
                            onClick={() => setAbrechnungFilter(o.wert)}
                            className={`rounded-lg border px-3 py-1.5 text-[11px] font-bold transition-all ${
                                abrechnungFilter === o.wert
                                    ? 'border-slate-800 bg-slate-800 text-white shadow-sm'
                                    : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                            }`}
                        >
                            {o.text}
                            <span className={abrechnungFilter === o.wert ? 'ml-1.5 text-slate-300' : 'ml-1.5 text-slate-400'}>
                                {abrechnungZahlen[o.wert]}
                            </span>
                        </button>
                    ))}
                </div>
                {/* Ohne diesen Satz sähe ein leerer Filter aus wie „nichts zu tun",
                    obwohl in Wahrheit nur die Angabe fehlt. Genau die Klasse
                    stiller Fehlaussage, die dieses Modul aufdecken soll. */}
                {abrechnungFilter !== 'alle' && abrechnungZahlen.unbekannt > 0 && (
                    <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
                        {abrechnungZahlen.unbekannt}{' '}
                        {abrechnungZahlen.unbekannt === 1 ? 'Befund ist' : 'Befunde sind'} hier
                        ausgeblendet: zu {abrechnungZahlen.unbekannt === 1 ? 'ihm' : 'ihnen'} hat der
                        Nachtlauf noch keine Abrechnungslage geschrieben. Sichtbar unter „Alle".
                    </p>
                )}
            </div>

            {/* Der wichtigste Zustand bekommt eine eigene Zeile, damit er nicht
                zwischen den uebrigen Befunden untergeht. */}
            {aufgelebteAnzahl > 0 && (
                <div className="rounded-xl border border-rose-300 bg-rose-50 p-3 flex gap-2">
                    <RotateCcw className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-rose-800 leading-relaxed">
                        <strong>{aufgelebteAnzahl} quittierte Befunde sind wieder aufgelebt.</strong>{' '}
                        Die Abweichung hat sich seit dem Abzeichnen verändert — die Quittierung gilt
                        dafür nicht mehr.
                    </p>
                </div>
            )}

            {(fehler || error) && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 flex gap-2">
                    <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-rose-800 leading-relaxed">{fehler || error}</p>
                </div>
            )}

            {isLoading && (
                <div className="flex items-center gap-2 text-sm text-slate-500 p-6 justify-center">
                    <Loader2 className="w-4 h-4 animate-spin" /> Lädt…
                </div>
            )}

            {!isLoading && !fehler && !error && gruppen.length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center">
                    <ShieldCheck className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    {/* Bei gesetztem Filter darf hier NICHT „nichts gefunden" stehen —
                        es ist nur nichts sichtbar. */}
                    <p className="text-sm text-slate-500">
                        {abrechnungFilter !== 'alle'
                            ? 'Kein Befund in dieser Abrechnungslage. Unter „Alle" stehen die übrigen.'
                            : findings.length === 0
                                ? 'Kein offener Befund. Der letzte Lauf hat nichts gefunden.'
                                : !zeigeArchivierte && grundmenge.length === 0
                                    ? `Kein offener Befund in laufenden Projekten. ${archiv.ausgeblendet} ${archiv.ausgeblendet === 1 ? 'Befund liegt' : 'Befunde liegen'} in archivierten Projekten.`
                                    : 'Kein offener Befund — alle sind quittiert.'}
                    </p>
                </div>
            )}

            <div className="space-y-5">
                {gruppen.map((g) => {
                    const kannSpringen = !!rohesProjekt(g.projectId);

                    return (
                        <div key={g.projectId} className="space-y-2">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="font-bold text-slate-800 truncate">
                                    <span className="text-slate-400 mr-2">{g.nummer}</span>
                                    {g.name}
                                    <span className="ml-2 text-xs font-medium text-slate-400">
                                        {g.befunde.length} {g.befunde.length === 1 ? 'Befund' : 'Befunde'}
                                    </span>
                                </p>
                                {kannSpringen ? (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => springen(g.projectId)}
                                        icon={<ExternalLink className="w-4 h-4" />}
                                    >
                                        Projekt öffnen
                                    </Button>
                                ) : (
                                    <span className="text-[11px] text-slate-400">
                                        Projekt nicht in Ihrer Liste
                                    </span>
                                )}
                            </div>

                            {g.befunde.map((f) => {
                                const aufgelebt = istWiederAufgelebt(f);
                                const quittiert = istQuittiert(f);
                                const meta = SCHWERE_META[f.severity] || SCHWERE_META.hinweis;
                                const beschaeftigt = busyId === f.id || sofortId === f.id;
                                const nachbuchbar = f.ruleId === UNBOOKED_RULE_ID
                                    && unbookedRows.some((r) => r.report_history_id === f.scopeId);
                                const sofort = sofortMeldung[f.id];

                                return (
                                    <div
                                        key={f.id}
                                        className={`rounded-2xl border p-4 bg-white ${
                                            aufgelebt
                                                ? 'border-rose-400 ring-2 ring-rose-100 shadow-sm'
                                                : quittiert
                                                    ? 'border-slate-200 opacity-70'
                                                    : f.severity === 'kritisch'
                                                        ? 'border-rose-200 shadow-sm'
                                                        : 'border-slate-200'
                                        }`}
                                    >
                                        <div className="flex flex-wrap items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${meta.badge}`}>
                                                        {meta.text}
                                                    </span>
                                                    <span className="font-mono text-[11px] font-bold bg-slate-50 border border-slate-200 text-slate-600 px-2 py-0.5 rounded-lg">
                                                        {f.ruleId}
                                                    </span>
                                                    {aufgelebt && (
                                                        <span className="text-[11px] font-bold px-2.5 py-1 rounded-full border bg-rose-600 text-white border-rose-600 flex items-center gap-1">
                                                            <RotateCcw className="w-3 h-3" /> Wieder aufgelebt
                                                        </span>
                                                    )}
                                                    {quittiert && !aufgelebt && (
                                                        <span className="text-[11px] font-bold px-2.5 py-1 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                                                            Quittiert
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-slate-800 mt-2 break-words">{f.message}</p>
                                                <p className="text-xs text-slate-500 mt-1 break-words">
                                                    {f.scopeLabel || f.scopeId}
                                                </p>
                                                <div>
                                                    <AbrechnungKennzeichen lage={abrechnungslage(f)} />
                                                </div>
                                            </div>

                                            <div className="text-right shrink-0">
                                                {(f.expected !== undefined || f.actual !== undefined) && (
                                                    <p className="text-xs text-slate-600 font-mono">
                                                        Soll {zahlText(f.expected)} · Ist {zahlText(f.actual)}
                                                    </p>
                                                )}
                                                <p className="text-[11px] text-slate-400 mt-0.5" title={`Erstsichtung: ${datum(f.firstSeenAt)}`}>
                                                    {seitText(f.firstSeenAt)}
                                                </p>
                                            </div>
                                        </div>

                                        {quittiert && (
                                            <p className={`text-xs mt-3 rounded-xl p-2.5 whitespace-pre-wrap break-words ${
                                                aufgelebt ? 'bg-rose-50 text-rose-800' : 'bg-slate-50 text-slate-600'
                                            }`}>
                                                Quittiert von <strong>{f.acknowledgedByName || '—'}</strong> am{' '}
                                                {datum(f.acknowledgedAt)}
                                                {f.acknowledgedReason ? `: ${f.acknowledgedReason}` : '.'}
                                                {aufgelebt && (
                                                    <>
                                                        {' '}Die Zahlen haben sich seither geändert — bitte erneut
                                                        ansehen und neu quittieren.
                                                    </>
                                                )}
                                            </p>
                                        )}

                                        {sofort && (
                                            <p className={`text-xs mt-3 rounded-xl p-2.5 whitespace-pre-line break-words ${
                                                sofort.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-900'
                                            }`}>
                                                {sofort.text}
                                            </p>
                                        )}

                                        {quittung?.id === f.id ? (
                                            <div className="mt-3">
                                                <textarea
                                                    autoFocus
                                                    rows={2}
                                                    value={quittung.grund}
                                                    onChange={(e) => setQuittung({ id: f.id, grund: e.target.value })}
                                                    placeholder="Warum ist das so in Ordnung? (bleibt am Befund stehen)"
                                                    className="w-full rounded-xl border border-slate-200 p-2.5 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 resize-none"
                                                />
                                                <div className="flex justify-end gap-2 mt-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => setQuittung(null)}
                                                        disabled={beschaeftigt}
                                                        icon={<X className="w-4 h-4" />}
                                                    >
                                                        Zurück
                                                    </Button>
                                                    {/* Ohne Begruendung geht es nicht — ein Haken ohne
                                                        Satz waere in vier Wochen nicht mehr zu deuten,
                                                        und die Datenbank weist ihn ohnehin ab. */}
                                                    <Button
                                                        variant="primary"
                                                        size="sm"
                                                        disabled={beschaeftigt || quittung.grund.trim().length === 0}
                                                        loading={beschaeftigt}
                                                        onClick={() => void quittieren(f, quittung.grund)}
                                                        icon={<Check className="w-4 h-4" />}
                                                    >
                                                        Quittierung speichern
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="mt-3 flex flex-wrap justify-end gap-2">
                                                {/* Bei einem quittierten Befund mit unveraendertem
                                                    Fingerabdruck gibt es NICHTS zu quittieren: die RPC
                                                    ist dort ein no-op und verwirft eine neu getippte
                                                    Begruendung stillschweigend. Ein Knopf, der sich
                                                    erfolgreich schliesst und nichts tut, ist genau die
                                                    Klasse Fehlaussage, gegen die dieses Modul gebaut
                                                    ist. */}
                                                {/* Nachbuchen gibt es NUR fuer den einen Befund, der
                                                    eine fehlende Buchung meldet, und nur solange die
                                                    Sicht die Zeile noch fuehrt. Ist sie weg, wurde
                                                    inzwischen gebucht oder ausgenommen — dann waere der
                                                    Knopf eine Einladung zur zweiten Buchung. */}
                                                {nachbuchbar && (
                                                    <>
                                                        {/* Schnellweg: bucht ohne Fenster, wenn die Vorschau
                                                            weder Riegel noch Warnung hat — sonst oeffnet er
                                                            das Fenster mit genau diesen Hinweisen. */}
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            disabled={beschaeftigt}
                                                            loading={sofortId === f.id}
                                                            onClick={() => void sofortBuchen(f)}
                                                            icon={<Zap className="w-4 h-4" />}
                                                            title="Bucht sofort auf die vorgeschlagene Position, wenn nichts dagegen spricht. Bei Hinweisen öffnet sich das Fenster."
                                                        >
                                                            Sofort buchen
                                                        </Button>
                                                        <Button
                                                            variant="secondary"
                                                            size="sm"
                                                            disabled={beschaeftigt}
                                                            onClick={() => setRebookScopeId(f.scopeId)}
                                                            icon={<Receipt className="w-4 h-4" />}
                                                        >
                                                            Nachbuchen …
                                                        </Button>
                                                    </>
                                                )}
                                                {(!quittiert || aufgelebt) && (
                                                    <Button
                                                        variant="secondary"
                                                        size="sm"
                                                        disabled={beschaeftigt}
                                                        onClick={() => setQuittung({ id: f.id, grund: '' })}
                                                        icon={<Check className="w-4 h-4" />}
                                                    >
                                                        {aufgelebt ? 'Neu quittieren' : 'Quittieren'}
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>

            {rebookRow && (
                <ReportRebookDialog
                    row={rebookRow}
                    abrechnung={findings.find((f) => f.ruleId === UNBOOKED_RULE_ID && f.scopeId === rebookScopeId)?.details?.abrechnung as any}
                    onMarkExempt={async (id, grund) => { await markExempt(id, grund); }}
                    onDone={() => { void reloadUnbooked(); void reload(); }}
                    onClose={() => setRebookScopeId(null)}
                />
            )}
        </div>
    );
};
